// src/pages/AdminTickets.js
//
// Admin — Support Tickets
//
// Shows every open support ticket (from the app AND from the
// Telegram bot). Telegram tickets carry a "📨 Telegram" badge.
// The admin writes a reply and resolves — for Telegram tickets the
// reply is delivered straight into the user's Telegram chat by the
// backend; for app users they get a push notification and see the
// reply in their ticket history.

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const SupportAPI = axios.create({
  baseURL: "https://alkebulanfkalertbackend.onrender.com/api/support",
});
SupportAPI.interceptors.request.use((req) => {
  const token = localStorage.getItem("adminToken");
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

const PRIORITY_STYLES = {
  urgent: "bg-red-100 text-red-700 border-red-200",
  high: "bg-amber-100 text-amber-700 border-amber-200",
  normal: "bg-gray-100 text-gray-600 border-gray-200",
};

const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [resolvingId, setResolvingId] = useState(null);
  const [notice, setNotice] = useState("");

  const fetchTickets = useCallback(async () => {
    try {
      const res = await SupportAPI.get("/tickets");
      setTickets(Array.isArray(res.data) ? res.data : []);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Could not load tickets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    const interval = setInterval(fetchTickets, 30000);
    return () => clearInterval(interval);
  }, [fetchTickets]);

  const resolveTicket = async (ticket, withReply) => {
    const reply = (replyDrafts[ticket._id] || "").trim();
    if (withReply && !reply) {
      setNotice("⚠️ Write a reply first, or use “Resolve without reply”.");
      return;
    }
    setResolvingId(ticket._id);
    setNotice("");
    try {
      await SupportAPI.patch(`/tickets/${ticket._id}/resolve`, {
        adminReply: withReply ? reply : "",
      });
      setNotice(
        ticket.source === "telegram" && withReply
          ? "✅ Resolved — the reply was delivered to the user's Telegram."
          : "✅ Ticket resolved."
      );
      setOpenId(null);
      await fetchTickets();
    } catch (err) {
      setNotice("❌ " + (err.response?.data?.message || "Could not resolve ticket"));
    } finally {
      setResolvingId(null);
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return "";
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900">🎫 Support Tickets</h2>
          <p className="text-sm text-gray-500 mt-1">
            Open tickets from the app and the Telegram bot. Replies to Telegram
            tickets are delivered into the user's chat automatically.
          </p>
        </div>
        <button
          onClick={() => { setLoading(true); fetchTickets(); }}
          className="px-3 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition"
        >
          ↻ Refresh
        </button>
      </div>

      {notice && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
          {notice}
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-500 py-16">Loading tickets…</div>
      ) : error ? (
        <div className="text-center text-red-600 py-16">{error}</div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <div className="text-4xl mb-3">🎉</div>
          <div className="font-semibold text-gray-800">No open tickets</div>
          <div className="text-sm text-gray-500 mt-1">
            Every user has been answered. New tickets appear here automatically.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => {
            const isOpen = openId === t._id;
            return (
              <div
                key={t._id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setOpenId(isOpen ? null : t._id)}
                  className="w-full text-left p-4 flex items-start justify-between gap-3 hover:bg-gray-50 transition"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 truncate">
                        {t.subject || "(no subject)"}
                      </span>
                      {t.source === "telegram" && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200">
                          📨 Telegram
                        </span>
                      )}
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${
                          PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.normal
                        }`}
                      >
                        {t.priority || "normal"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1.5">
                      {t.userName || "Unknown"}
                      {t.userEmail ? ` · ${t.userEmail}` : ""}
                      {t.userPlan ? ` · ${t.userPlan} plan` : ""}
                      {" · "}
                      {formatDate(t.createdAt)}
                    </div>
                  </div>
                  <span className="text-gray-400 text-lg flex-shrink-0">
                    {isOpen ? "▾" : "▸"}
                  </span>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50">
                    <div className="text-sm text-gray-800 whitespace-pre-wrap bg-white border border-gray-200 rounded-lg p-3 mb-4">
                      {t.message}
                    </div>

                    <textarea
                      value={replyDrafts[t._id] || ""}
                      onChange={(e) =>
                        setReplyDrafts((prev) => ({ ...prev, [t._id]: e.target.value }))
                      }
                      placeholder="Write your reply… (delivered to the user)"
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-3"
                    />

                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => resolveTicket(t, true)}
                        disabled={resolvingId === t._id}
                        className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
                      >
                        {resolvingId === t._id ? "Sending…" : "✅ Send Reply & Resolve"}
                      </button>
                      <button
                        onClick={() => resolveTicket(t, false)}
                        disabled={resolvingId === t._id}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
                      >
                        Resolve without reply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminTickets;
